import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { BaseFormControl } from '../base-control';
import { FormImageField } from '../form-field';
import { ImageService } from '../../../global/images/image.service';
import { HttpClientWithInFlightCache } from '../../../global/httpClient';
import { FileBrowserModalComponent } from '../../file-browser/file-browser-modal.component';
import { UploadStateService } from './upload-state.service';
import { ImageCropUploadService, ImageUploadVariant } from './image-crop-upload.service';

@Component({
    standalone: false,
    selector: 'abs-file-upload',
    template: `
        <div class="form-group file-upload" *ngIf="parentFormGroup" [formGroup]="parentFormGroup">
            <label *ngIf="!field.hideLabel">
                {{ field.label || ($any(field.name) | formLabel) }}
                <ion-icon class="info" *ngIf="field.description" name="information-circle" (click)="showDescription($event)"></ion-icon>
            </label>

            <div class="image-preview" *ngIf="previewUrl">
                <img [src]="previewUrl" (load)="onImageLoad()" (error)="onImageError()" [style.display]="imageLoadError ? 'none' : ''" />
                <div class="image-error-placeholder" *ngIf="imageLoadError">
                    <ion-icon name="image-outline"></ion-icon>
                    <span>Preview unavailable</span>
                </div>
            </div>

            <div class="image-controls">
                <input
                    type="file"
                    #fileInput
                    [accept]="field.accept || 'image/*'"
                    (change)="onFileSelected($event)"
                    style="display: none"
                />
                <ion-button class="btn-sm" (click)="fileInput.click()" [disabled]="uploading">
                    {{ previewUrl ? 'Replace' : field.crop ? 'Upload & Crop' : 'Upload' }}
                </ion-button>
                <ion-button *ngIf="hasSourceImage" class="btn-sm" color="tertiary" (click)="reCrop()" [disabled]="uploading">
                    Re-crop
                </ion-button>
                <ion-button class="btn-sm" color="secondary" (click)="browseExisting()" [disabled]="uploading">
                    Browse Existing
                </ion-button>
                <ion-button class="btn-sm" color="danger" *ngIf="previewUrl" (click)="removeImage()" [disabled]="uploading">
                    Remove
                </ion-button>
                <ion-spinner *ngIf="uploading" name="crescent"></ion-spinner>
                <span class="upload-error" *ngIf="uploadError">{{ uploadError }}</span>
            </div>

            <div class="crop-help" *ngIf="field.crop">Uploaded images are cropped to {{ field.crop.width }}×{{ field.crop.height }}.</div>
        </div>
    `,
    styles: [
        `
            .image-preview {
                margin: 8px 0;
            }
            .image-preview img {
                max-width: 200px;
                max-height: 200px;
                border-radius: 4px;
                border: 1px solid #ccc;
                object-fit: contain;
            }
            .image-error-placeholder {
                display: flex;
                align-items: center;
                gap: 6px;
                width: 200px;
                height: 60px;
                border-radius: 4px;
                border: 1px dashed #ccc;
                justify-content: center;
                color: #999;
                font-size: 0.82em;
            }
            .image-controls {
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
            }
            .upload-error {
                color: #dc3545;
                font-size: 0.85em;
            }

            .crop-help {
                margin-top: 8px;
                font-size: 0.85em;
                color: var(--ion-color-medium-shade, #666);
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadComponent extends BaseFormControl implements OnInit {
    private imageService = inject(ImageService);
    private http = inject(HttpClientWithInFlightCache);
    private cdr = inject(ChangeDetectorRef);
    private fileModalController = inject(ModalController);
    private uploadState = inject(UploadStateService);
    private cropUpload = inject(ImageCropUploadService);

    @Input()
    field: FormImageField;

    previewUrl: string | null = null;
    sourcePreviewUrl: string | null = null;
    imageLoadError = false;
    uploading = false;
    uploadError: string | null = null;

    /** Set the local uploading flag and keep the shared UploadStateService in sync. */
    private setUploading(value: boolean) {
        if (value === this.uploading) return;
        this.uploading = value;
        value ? this.uploadState.begin() : this.uploadState.end();
    }

    /** True when the source field has a saved image ID — used to show the Re-crop button
     *  regardless of whether the image URL successfully loaded in the browser. */
    get hasSourceImage(): boolean {
        return !!(this.field?.sourceField && this.parentFormGroup?.get(this.field.sourceField)?.value);
    }

    ngOnInit() {
        super.ngOnInit();
        this.updatePreview();

        // Keep preview in sync if the control value changes externally
        this.parentFormGroup.get(this.field.name)?.valueChanges.subscribe(() => {
            this.updatePreview();
            this.cdr.markForCheck();
        });

        if (this.field.sourceField) {
            this.updateSourcePreview();
            this.parentFormGroup.get(this.field.sourceField)?.valueChanges.subscribe(() => {
                this.updateSourcePreview();
                this.cdr.markForCheck();
            });
        }
    }

    async browseExisting() {
        const modal = await this.fileModalController.create({
            component: FileBrowserModalComponent
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data?.imageId) {
            this.parentFormGroup.get(this.field.name)?.setValue(data.imageId);
            this.parentFormGroup.get(this.field.name)?.markAsDirty();
            // When browsing existing, the selected image is also the source
            if (this.field.sourceField) {
                this.parentFormGroup.get(this.field.sourceField)?.setValue(data.imageId);
                this.parentFormGroup.get(this.field.sourceField)?.markAsDirty();
            }
            if (this.field.sourceFileNameField && data.filename) {
                this.parentFormGroup.get(this.field.sourceFileNameField)?.setValue(data.filename);
                this.parentFormGroup.get(this.field.sourceFileNameField)?.markAsDirty();
            }
            this.previewUrl = this.imageService.url(data.imageId);
            this.cdr.markForCheck();
        }
    }

    onFileSelected(event: Event) {
        void this.handleFileSelected(event);
    }

    private async handleFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        input.value = '';

        if (this.field.crop) {
            const initialCropWidth: number | undefined = this.field.cropWidthField
                ? this.parentFormGroup.get(this.field.cropWidthField)?.value || undefined
                : undefined;
            const initialCropHeight: number | undefined = this.field.cropHeightField
                ? this.parentFormGroup.get(this.field.cropHeightField)?.value || undefined
                : undefined;
            const crop = {
                width: initialCropWidth ?? this.field.crop.width,
                height: initialCropHeight ?? this.field.crop.height,
                minZoom: this.field.crop.minZoom
            };
            const gameId = this.formValue?.gameId || '';
            const category = this.field.category || 'unit-template';

            if (this.field.sourceField) {
                this.setUploading(true);
                this.uploadError = null;
                this.cdr.markForCheck();
                try {
                    const initialZoom = this.field.zoomField ? (this.parentFormGroup.get(this.field.zoomField)?.value ?? 1) : 1;
                    const initialTransform = this.field.transformField
                        ? this.parentFormGroup.get(this.field.transformField)?.value
                        : undefined;
                    const result = await this.cropUpload.uploadWithCrop(
                        file,
                        crop,
                        gameId,
                        category,
                        initialZoom,
                        initialTransform,
                        initialCropWidth,
                        initialCropHeight
                    );
                    if (!result) return; // user cancelled crop
                    this.parentFormGroup.get(this.field.sourceField)?.setValue(result.imageSourceId);
                    this.parentFormGroup.get(this.field.sourceField)?.markAsDirty();
                    if (this.field.sourceFileNameField) {
                        this.parentFormGroup.get(this.field.sourceFileNameField)?.setValue(result.imageSourceFileName);
                        this.parentFormGroup.get(this.field.sourceFileNameField)?.markAsDirty();
                    }
                    if (this.field.zoomField) {
                        this.parentFormGroup.get(this.field.zoomField)?.setValue(result.zoom);
                        this.parentFormGroup.get(this.field.zoomField)?.markAsDirty();
                    }
                    if (this.field.transformField) {
                        this.parentFormGroup.get(this.field.transformField)?.setValue(result.transform);
                        this.parentFormGroup.get(this.field.transformField)?.markAsDirty();
                    }
                    if (this.field.cropWidthField) {
                        this.parentFormGroup.get(this.field.cropWidthField)?.setValue(result.cropWidth);
                        this.parentFormGroup.get(this.field.cropWidthField)?.markAsDirty();
                    }
                    if (this.field.cropHeightField) {
                        this.parentFormGroup.get(this.field.cropHeightField)?.setValue(result.cropHeight);
                        this.parentFormGroup.get(this.field.cropHeightField)?.markAsDirty();
                    }
                    this.parentFormGroup.get(this.field.name)?.setValue(result.imageId);
                    this.parentFormGroup.get(this.field.name)?.markAsDirty();
                    this.revokeBlobUrl(this.previewUrl);
                    this.previewUrl = result.previewUrl;
                    this.sourcePreviewUrl = this.imageService.url(result.imageSourceId);
                    this.imageLoadError = false;
                } catch (err) {
                    this.uploadError = 'Upload failed. Please try again.';
                    console.error('File upload error:', err);
                } finally {
                    this.setUploading(false);
                    this.cdr.markForCheck();
                }
            } else {
                const imageBase64 = await this.fileToBase64(file);
                const result = await this.cropUpload.openCropper({ imageBase64 }, crop);
                if (!result) return;
                const croppedFile = new File([result.blob], file.name, { type: result.blob.type });
                this.uploadFile(croppedFile, 'cropped');
            }
        } else {
            this.uploadFile(file);
        }
    }

    async reCrop() {
        const sourceId = this.parentFormGroup.get(this.field.sourceField)?.value;
        if (!sourceId) return;

        const sourceFileName = this.field.sourceFileNameField ? this.parentFormGroup.get(this.field.sourceFileNameField)?.value : null;
        if (!sourceFileName) throw new Error('Source filename not found — re-crop requires a stored filename');

        const initialCropWidth: number | undefined = this.field.cropWidthField
            ? this.parentFormGroup.get(this.field.cropWidthField)?.value || undefined
            : undefined;
        const initialCropHeight: number | undefined = this.field.cropHeightField
            ? this.parentFormGroup.get(this.field.cropHeightField)?.value || undefined
            : undefined;
        const crop = {
            width: initialCropWidth ?? this.field.crop.width,
            height: initialCropHeight ?? this.field.crop.height,
            minZoom: this.field.crop?.minZoom
        };
        const gameId = this.formValue?.gameId || '';
        const category = this.field.category || 'unit-template';
        const initialZoom = this.field.zoomField ? (this.parentFormGroup.get(this.field.zoomField)?.value ?? 1) : 1;
        const initialTransform = this.field.transformField
            ? (this.parentFormGroup.get(this.field.transformField)?.value ?? undefined)
            : undefined;

        this.setUploading(true);
        this.uploadError = null;
        this.cdr.markForCheck();
        try {
            const result = await this.cropUpload.reCrop(
                sourceId,
                sourceFileName,
                crop,
                gameId,
                category,
                initialZoom,
                initialTransform,
                initialCropWidth,
                initialCropHeight
            );
            if (!result) return; // user cancelled
            this.parentFormGroup.get(this.field.name)?.setValue(result.imageId);
            this.parentFormGroup.get(this.field.name)?.markAsDirty();
            if (this.field.zoomField) {
                this.parentFormGroup.get(this.field.zoomField)?.setValue(result.zoom);
                this.parentFormGroup.get(this.field.zoomField)?.markAsDirty();
            }
            if (this.field.transformField) {
                this.parentFormGroup.get(this.field.transformField)?.setValue(result.transform);
                this.parentFormGroup.get(this.field.transformField)?.markAsDirty();
            }
            if (this.field.cropWidthField) {
                this.parentFormGroup.get(this.field.cropWidthField)?.setValue(result.cropWidth);
                this.parentFormGroup.get(this.field.cropWidthField)?.markAsDirty();
            }
            if (this.field.cropHeightField) {
                this.parentFormGroup.get(this.field.cropHeightField)?.setValue(result.cropHeight);
                this.parentFormGroup.get(this.field.cropHeightField)?.markAsDirty();
            }
            if (this.field.cropXField) {
                this.parentFormGroup.get(this.field.cropXField)?.setValue(result.cropX);
                this.parentFormGroup.get(this.field.cropXField)?.markAsDirty();
            }
            if (this.field.cropYField) {
                this.parentFormGroup.get(this.field.cropYField)?.setValue(result.cropY);
                this.parentFormGroup.get(this.field.cropYField)?.markAsDirty();
            }
            this.revokeBlobUrl(this.previewUrl);
            this.previewUrl = result.previewUrl;
            this.imageLoadError = false;
        } catch (err) {
            this.uploadError = 'Upload failed. Please try again.';
            console.error('Re-crop upload error:', err);
        } finally {
            this.setUploading(false);
            this.cdr.markForCheck();
        }
    }

    private uploadFile(file: File, variant?: ImageUploadVariant) {
        const fileName = file.name;
        if (!fileName) throw new Error('Cannot upload file without a name');

        this.setUploading(true);
        this.uploadError = null;
        this.cdr.markForCheck();

        this.cropUpload.uploadToApi(file, fileName, this.formValue?.gameId || '', this.field.category || 'unit-template', variant).then(
            (res) => {
                this.parentFormGroup.get(this.field.name)?.setValue(res.id);
                this.parentFormGroup.get(this.field.name)?.markAsDirty();
                this.revokeBlobUrl(this.previewUrl);
                this.previewUrl = URL.createObjectURL(file);
                this.imageLoadError = false;
                this.setUploading(false);
                this.cdr.markForCheck();
            },
            (err) => {
                this.uploadError = 'Upload failed. Please try again.';
                this.setUploading(false);
                this.cdr.markForCheck();
                console.error('File upload error:', err);
            }
        );
    }

    private uploadFileToApi(file: File | Blob, fileName: string): Promise<{ id: string }> {
        const formData = new FormData();
        formData.append('file', file, fileName);
        formData.append('gameId', this.formValue?.gameId || '');
        formData.append('category', this.field.category || 'unit-template');

        const url = `${this.config.apiBaseUrl}/images/upload`;
        return this.http.post(url, formData).toPromise() as Promise<{ id: string }>;
    }

    removeImage() {
        const control = this.parentFormGroup.get(this.field.name);
        control?.setValue(null);
        control?.markAsDirty();

        if (this.field.sourceField) {
            const sourceControl = this.parentFormGroup.get(this.field.sourceField);
            sourceControl?.setValue(null);
            sourceControl?.markAsDirty();
        }

        if (this.field.sourceFileNameField) {
            const sourceFileNameControl = this.parentFormGroup.get(this.field.sourceFileNameField);
            sourceFileNameControl?.setValue(null);
            sourceFileNameControl?.markAsDirty();
        }

        this.revokeBlobUrl(this.previewUrl);
        this.previewUrl = null;
        this.sourcePreviewUrl = null;
        this.imageLoadError = false;
        this.cdr.markForCheck();
    }

    onImageLoad() {
        if (this.imageLoadError) {
            this.imageLoadError = false;
            this.cdr.markForCheck();
        }
    }

    onImageError() {
        // Don't null previewUrl — the URL is still the intended image (may be a transient
        // S3 propagation lag). Track the error visually so the Re-crop button stays accessible.
        this.imageLoadError = true;
        this.cdr.markForCheck();
    }

    private updatePreview() {
        const imageId = this.parentFormGroup.get(this.field.name)?.value;
        this.revokeBlobUrl(this.previewUrl);
        let url = this.imageService.url(imageId);
        // Add cache buster if lastUpdated field is configured
        if (url && this.field.lastUpdatedField) {
            const lastUpdated = this.parentFormGroup.get(this.field.lastUpdatedField)?.value;
            if (lastUpdated) {
                url = `${url}?v=${lastUpdated}`;
            }
        }
        this.previewUrl = url;
        this.imageLoadError = false; // reset error state when the intended image changes
    }

    private revokeBlobUrl(url: string | null) {
        if (url?.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    }

    private updateSourcePreview() {
        const sourceId = this.parentFormGroup.get(this.field.sourceField)?.value;
        let url = this.imageService.url(sourceId);
        // Add cache buster if lastUpdated field is configured
        if (url && this.field.lastUpdatedField) {
            const lastUpdated = this.parentFormGroup.get(this.field.lastUpdatedField)?.value;
            if (lastUpdated) {
                url = `${url}?v=${lastUpdated}`;
            }
        }
        this.sourcePreviewUrl = url;
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}
